import React, { useState, memo } from 'react'
import { ReactComponent as GreenCheck } from 'assets/illustrations/GreenCheck.svg'
import { useCreateRate } from 'apps/marketplace/apiTypes/requestForProposalLaneCarrierRates'
import { useCreateSourcedCarrierRates } from 'apps/marketplace/apiTypes/sourcedCarrierRates'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { IRFPLane } from 'apps/marketplace/utils/api/rfpLanes'
import { ShipperRateObjectAugmented } from 'apps/marketplace/utils/hooks/AwardedLanes/useAwardedLanesApi'
import { useRFPLaneCreateRateForm } from 'apps/marketplace/utils/hooks/useRFPLaneRateForm'
import { FullPageOverlay } from 'transfix-ui'
import { getError } from 'utils/parseError'
import { RFPLaneSearch } from './add-rate-modal/RFPLaneSearch'
import { RateForm } from './rate-form/RateForm'

interface IAddRateModalProps {
  closeAction: (type?: string) => void
  awardedLane?: ShipperRateObjectAugmented
  awardedLaneHeader?: React.ReactElement
}

export const AddRateModal = memo<IAddRateModalProps>(
  ({ awardedLane, awardedLaneHeader, closeAction }) => {
    const { trackEvent } = useMPAnalytics()
    const [selectedRFPLane, setSelectedRFPLane] = useState<IRFPLane | undefined>(undefined)
    const [step, setStep] = useState<number>(0)
    const onSuccess = () => setStep(step + 1)
    const {
      control,
      errors: formErrors,
      formState,
      handleSubmit,
      setValue,
    } = useRFPLaneCreateRateForm({})
    const { error, loading, mutate: createLaneRate } = useCreateRate({})
    const { mutate: createLaneRateOnAwardedLane } = useCreateSourcedCarrierRates({
      shipperRateId: awardedLane && awardedLane.id ? awardedLane.id : 0,
    })

    const addRFPLaneRateWithTracking = () => {
      trackEvent('RFP Lanes', 'SAVED_ADD_RATE_MODAL', { id: selectedRFPLane?.id })
      handleSubmit(data => {
        if (selectedRFPLane) {
          createLaneRate({
            rfpLaneCarrierRate: {
              carrierApplicationId: data.carrierApplicationId,
              maxCapacity: data.maxCapacity,
              minCapacity: data.minCapacity,
              canDoWeekends: data.canDoWeekends,
              confirmed: data.confirmed,
              rateAttributes: {
                amount: data.rateAttributes.amount,
                rateTypeKey: data.rateAttributes.rateTypeKey,
              },
              requestForProposalLaneId: selectedRFPLane.id,
            },
          }).then(() => onSuccess())
        } else if (awardedLane) {
          createLaneRateOnAwardedLane({
            sourcedCarrierRates: [
              {
                carrierApplicationId: Number(data.carrierApplicationId),
                maxCapacity: data.maxCapacity ? data.maxCapacity : 0,
                minCapacity: data.minCapacity ? data.minCapacity : 0,
                canDoWeekends: data.canDoWeekends,
                rateAttributes: {
                  amount: Number(data.rateAttributes.amount),
                  rateTypeKey: data.rateAttributes.rateTypeKey,
                },
              },
            ],
          }).then(() => onSuccess())
        }
      })()
    }

    const forwardStep = () => {
      trackEvent('RFP Lanes', 'CONTINUED_ADD_RATE_MODAL', { id: selectedRFPLane?.id })
      setStep(step + 1)
    }
    const backwardStep = () => {
      trackEvent('RFP Lanes', 'BACK_ADD_RATE_MODAL')
      if (!loading) {
        setStep(step - 1)
      }
    }
    const closeModal = () => {
      trackEvent('RFP Lanes', 'CLOSED_ADD_RATE_MODAL')
      if (!loading) {
        closeAction()
      }
    }
    const closeAndReload = () => closeAction('reload')

    const searchEditConfig = !awardedLane
      ? [
          {
            title: 'Search RFP Lane',
            primaryActionConfig: {
              onClick: forwardStep,
              buttonText: 'Continue',
              disabled: !selectedRFPLane,
            },
            secondaryActionConfig: {
              onClick: closeModal,
              buttonText: 'Cancel',
            },
          },
        ]
      : []

    const stepEditConfig = [
      ...searchEditConfig,
      {
        title: 'Add Rate to Lane',
        primaryActionConfig: {
          onClick: addRFPLaneRateWithTracking,
          buttonText: 'Save Changes',
          disabled: loading || !formState.isValid,
        },
        secondaryActionConfig: {
          onClick: closeModal,
          buttonText: 'Cancel',
          disabled: loading,
        },
      },
      {
        title: 'Rate Added',
        primaryActionConfig: {
          onClick: closeAndReload,
          buttonText: 'Close',
        },
        secondaryActionConfig: {
          onClick: backwardStep,
          buttonText: 'Add Another Rate',
        },
      },
    ]

    const searchStep = !awardedLane
      ? [
          <RFPLaneSearch
            key='step-1'
            selectedRFPLane={selectedRFPLane}
            setSelectedRFPLane={setSelectedRFPLane}
          />,
        ]
      : []

    const steps = [
      ...searchStep,
      <>
        {awardedLaneHeader}
        <RateForm
          key='step-2'
          selectedRFPLane={selectedRFPLane}
          control={control}
          setValue={setValue}
          capacityIsRange={false}
          formErrors={formErrors}
          loading={loading}
        />
      </>,
      <div key='step-3' className='flex horizontally-center vertically-center pa6'>
        <div>
          <div className='horizontally-center mb3'>
            <GreenCheck />
          </div>
          <b>Your rates have been added.</b> Select &#39;
          <b>Add Another Rate</b>&#39; to return to the RFP lane search results and continue adding
          rates.
        </div>
      </div>,
    ]

    return (
      <FullPageOverlay
        open
        onClose={closeModal}
        stepConfig={stepEditConfig}
        onPressBackButton={backwardStep}
        currentStep={step}
        errors={error ? [getError(error)] : []}
        renderAll>
        {steps}
      </FullPageOverlay>
    )
  }
)
